import { useCallback, useMemo, useState } from 'react'
import { ArrowUpRight, Search, X } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ALL,
  DENSITY_NOTE,
  KPIS,
  LIBRARY_PAGE,
  PRIVACY,
  RECORDS,
  SCOPING,
  SEARCH,
  SPLIT,
  SPLIT_SEGMENTS,
  TABLE,
  VERSION_CHIPS,
  VERTICAL_CHIPS,
  filterRecords,
  solutionHref,
  totalEffort,
  type SolvedBy,
  type SplitKey,
} from '@/demo/content/requirementsLibrary'
import { cn } from '@/lib/utils'

/**
 * 2.5 Library — the requirements library.
 *
 * The surface is a search box first and a table second: the library is only
 * useful if a consultant can put a client's own sentence into it and get back
 * how the group answered that sentence before, and what it cost. Search,
 * vertical and version all filter the same rows, and an empty result says so
 * with the way out attached.
 *
 * Nothing in the table names a client — that is the product's promise, stated
 * in the privacy panel and in the density note rather than left to be noticed.
 */

/** Locked pill semantics for this surface (StatusPill is never restyled). */
const SOLVED_TONE: Record<SolvedBy, StatusTone> = {
  'Template v4': 'proposed',
  'Module available': 'done',
  New: 'neutral',
}

const SPLIT_BAR: Record<SplitKey, string> = {
  standard: 'bg-done',
  library: 'bg-mint',
  'new-build': 'bg-neutral',
}

const SPLIT_INK: Record<SplitKey, string> = {
  standard: 'text-done',
  library: 'text-mint-ink',
  'new-build': 'text-neutral',
}

export default function RequirementsLibraryPage() {
  const [query, setQuery] = useState('')
  const [vertical, setVertical] = useState<string>(ALL)
  const [version, setVersion] = useState<string>(ALL)

  const rows = useMemo(() => filterRecords(RECORDS, query, vertical, version), [query, vertical, version])
  const hours = useMemo(() => totalEffort(rows), [rows])
  const segments = useMemo(
    () => SPLIT_SEGMENTS.map((s) => ({ ...s, style: { width: `${s.pct}%` } })),
    []
  )

  const filtered = query.trim().length > 0 || vertical !== ALL || version !== ALL

  const onSearchChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value),
    []
  )
  const onClearClicked = useCallback(() => {
    setQuery('')
    setVertical(ALL)
    setVersion(ALL)
  }, [])
  const onVerticalClicked = useCallback((key: string) => setVertical(key), [])
  const onVersionClicked = useCallback((key: string) => setVersion(key), [])

  return (
    <AppShell
      moduleLabel={LIBRARY_PAGE.moduleLabel}
      breadcrumb={LIBRARY_PAGE.breadcrumb}
      contextPill={LIBRARY_PAGE.contextPill}
      nav={libraryNav}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <header className="max-w-3xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{LIBRARY_PAGE.title}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">{LIBRARY_PAGE.standfirst}</p>
      </header>

      {/* ── Group figures ───────────────────────────────────────────────── */}
      <section className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Library at a glance">
        {KPIS.map((k) => (
          <KpiCard key={k.key} label={k.label} figure={k.figure} qualifier={k.qualifier} />
        ))}
      </section>

      {/* ── Search + filters ────────────────────────────────────────────── */}
      <section className="mt-6" aria-label="Search the requirements library">
        <label htmlFor="library-search" className="block">
          <SectionLabel>{SEARCH.label}</SectionLabel>
        </label>
        <div className="mt-1.5 flex max-w-2xl items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id="library-search"
              type="search"
              value={query}
              onChange={onSearchChanged}
              placeholder={SEARCH.placeholder}
              aria-describedby="library-search-hint"
              className="pl-9"
            />
          </div>
          {filtered && (
            <button
              type="button"
              onClick={onClearClicked}
              className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-[13px] text-ink-soft hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              {SEARCH.clearLabel}
            </button>
          )}
        </div>
        <p id="library-search-hint" className="mt-1.5 text-xs text-muted-foreground">
          {SEARCH.hint}
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-6">
          <div>
            <SectionLabel>{SEARCH.verticalLabel}</SectionLabel>
            <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={SEARCH.verticalLegend}>
              {VERTICAL_CHIPS.map((c) => {
                const active = c.key === vertical
                return (
                  <button
                    key={c.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onVerticalClicked(c.key)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] transition-colors',
                      active
                        ? 'border-mint bg-accent font-semibold text-accent-foreground'
                        : 'border-border bg-card text-ink-soft hover:bg-muted'
                    )}
                  >
                    {c.label}
                    <span className="v-figure tabular-nums text-muted-foreground">{c.count}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <SectionLabel>{SEARCH.versionLabel}</SectionLabel>
            <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={SEARCH.versionLegend}>
              {VERSION_CHIPS.map((c) => {
                const active = c.key === version
                return (
                  <button
                    key={c.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onVersionClicked(c.key)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] transition-colors',
                      active
                        ? 'border-mint bg-accent font-semibold text-accent-foreground'
                        : 'border-border bg-card text-ink-soft hover:bg-muted'
                    )}
                  >
                    {c.label}
                    <span className="v-figure tabular-nums text-muted-foreground">{c.count}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Records ─────────────────────────────────────────────────────── */}
      <section aria-labelledby="records-heading" className="mt-5 rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{TABLE.label}</SectionLabel>
          <h2 id="records-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {TABLE.heading}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{TABLE.caption}</p>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{TABLE.columns.pattern}</TableHead>
              <TableHead>{TABLE.columns.vertical}</TableHead>
              <TableHead>{TABLE.columns.odoo}</TableHead>
              <TableHead>{TABLE.columns.solvedBy}</TableHead>
              <TableHead className="text-right">
                {TABLE.columns.effort}{' '}
                <span className="font-normal text-muted-foreground">{TABLE.effortHeaderNote}</span>
              </TableHead>
              <TableHead>{TABLE.columns.reusable}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const href = solutionHref(r)
              return (
                <TableRow key={r.id}>
                  <TableCell className="max-w-md px-4 whitespace-normal">
                    <span className="text-[13px] font-medium text-foreground">{r.pattern}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{r.detail}</span>
                  </TableCell>
                  <TableCell className="text-[13px] text-ink-soft">{r.vertical}</TableCell>
                  <TableCell className="v-figure text-[13px] tabular-nums text-ink-soft">
                    {TABLE.odooLabel(r.odooVersion)}
                  </TableCell>
                  <TableCell className="max-w-xs whitespace-normal">
                    {href ? (
                      <Link to={href} className="inline-flex items-center gap-1 hover:underline">
                        <StatusPill tone={SOLVED_TONE[r.solvedBy]}>{r.solvedBy}</StatusPill>
                        <ArrowUpRight className="h-3 w-3 text-accent-foreground" aria-hidden />
                        <span className="sr-only">— {TABLE.linkHint}</span>
                      </Link>
                    ) : (
                      <StatusPill tone={SOLVED_TONE[r.solvedBy]}>{r.solvedBy}</StatusPill>
                    )}
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {TABLE.solutionLead} <span className="font-mono">{r.solution}</span>
                    </span>
                  </TableCell>
                  <TableCell className="v-figure text-right text-[13px] tabular-nums text-ink-soft">
                    {r.effortH} {TABLE.effortUnit}
                  </TableCell>
                  <TableCell className="px-4 text-[13px] text-ink-soft">
                    {r.reuses > 0 ? (
                      <span className="v-figure tabular-nums">{TABLE.reuseLine(r.reuses)}</span>
                    ) : (
                      <span className="text-muted-foreground">{TABLE.reuseNone}</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="px-4 py-4 whitespace-normal">
                  <p className="text-[13px] text-muted-foreground">
                    {query.trim().length > 0 ? TABLE.emptyLine(query.trim()) : TABLE.emptyFiltersLine}
                  </p>
                  <button
                    type="button"
                    onClick={onClearClicked}
                    className="mt-2 inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-[13px] font-medium text-accent-foreground hover:bg-muted"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    {SEARCH.clearLabel}
                  </button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          <span className="v-figure tabular-nums">{TABLE.shownLine(rows.length, RECORDS.length)}</span>
          {' · '}
          <span className="v-figure tabular-nums">{TABLE.effortLine(hours)}</span>
        </p>
      </section>

      {/* ── Panels: scoping · split · privacy ───────────────────────────── */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <section aria-labelledby="scoping-heading" className="rounded-lg border border-border bg-accent px-4 py-4">
          <SectionLabel>{SCOPING.label}</SectionLabel>
          <h2 id="scoping-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {SCOPING.heading}
          </h2>
          <p className="mt-1.5 text-[13px] text-foreground">{SCOPING.lead}</p>
          <p className="mt-2 text-[13px] text-ink-soft">
            {SCOPING.reuseLine} —{' '}
            <Link to={SCOPING.reuseHref} className="font-medium text-accent-foreground hover:underline">
              {SCOPING.reuseLinkLabel}
            </Link>
          </p>
          <p className="mt-2 border-t border-mint/30 pt-2.5 text-xs text-muted-foreground">{SCOPING.closing}</p>
        </section>

        <section aria-labelledby="split-heading" className="rounded-lg border border-border bg-card px-4 py-4">
          <SectionLabel>{SPLIT.label}</SectionLabel>
          <h2 id="split-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {SPLIT.heading}
          </h2>

          <div
            className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={`${SPLIT.barLabel}: ${segments.map((s) => `${s.label} ${s.pct}%`).join(', ')}`}
          >
            {segments.map((s) => (
              <span key={s.key} className={cn('block h-full', SPLIT_BAR[s.key])} style={s.style} />
            ))}
          </div>

          <ul className="mt-3">
            {segments.map((s) => (
              <li key={s.key} className="flex items-baseline justify-between gap-2 py-1">
                <span className="flex items-center gap-1.5 text-[13px] text-foreground">
                  <span className={cn('h-2 w-2 rounded-sm', SPLIT_BAR[s.key])} aria-hidden />
                  {s.label}
                </span>
                <span className={cn('v-figure text-[13px] tabular-nums', SPLIT_INK[s.key])}>
                  {SPLIT.segmentLine(s)}
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-2 border-t border-border pt-2.5 text-xs text-muted-foreground">{SPLIT.note}</p>
          <p className="mt-1.5 text-xs text-muted-foreground">{SPLIT.exemplarNote}</p>
        </section>

        <section aria-labelledby="privacy-heading" className="rounded-lg border border-border bg-card px-4 py-4">
          <SectionLabel>{PRIVACY.label}</SectionLabel>
          <h2 id="privacy-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {PRIVACY.heading}
          </h2>
          <ul className="mt-2">
            {PRIVACY.strippings.map((line) => (
              <li key={line} className="v-row py-1.5 text-[13px] text-foreground">
                {line}
              </li>
            ))}
          </ul>
          <p className="mt-2 border-t border-border pt-2.5 text-xs text-muted-foreground">
            {PRIVACY.consentLine} —{' '}
            <Link to={PRIVACY.consentHref} className="font-medium text-accent-foreground hover:underline">
              {PRIVACY.consentLinkLabel}
            </Link>
          </p>
        </section>
      </div>

      <DensityNote>{DENSITY_NOTE}</DensityNote>
    </AppShell>
  )
}
